import { QueryClient } from "@tanstack/react-query";
import { auth } from '@/lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getTenantHeaders } from '@/lib/tenant-context';
import { getApiUrl } from '@/lib/api-config';

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }));
    throw error;
  }
}

async function getIdTokenSafe(): Promise<string | null> {
  if (auth.currentUser) {
    try { return await auth.currentUser.getIdToken(); } catch { return null; }
  }
  // Wait briefly for auth to initialize
  const user = await new Promise<import('firebase/auth').User | null>((resolve) => {
    const unsub = onAuthStateChanged(auth, (u) => { unsub(); resolve(u); });
    setTimeout(() => { unsub(); resolve(null); }, 800);
  });
  if (!user) return null;
  try { return await user.getIdToken(); } catch { return null; }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const tenantHeaders = getTenantHeaders();
  
  // Ensure URL starts with /api/v1 if it's a relative path
  const fullUrl = url.startsWith('/api/v1') ? getApiUrl(url.replace('/api/v1', '')) : url;
  
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...tenantHeaders,
    ...(data ? { "Content-Type": "application/json" } : {}),
  };
  
  const token = await getIdTokenSafe();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  
  /*console.log('🔍 QueryClient API Call Debug:', {
    method,
    url: fullUrl,
    hasToken: !!token,
    tenantHeaders
  });*/
  
  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  /*console.log('🔍 QueryClient API Response:', {
    status: res.status,
    statusText: res.statusText,
    url: res.url
  });*/

  await throwIfResNotOk(res);
  return res;
}

// Multipart/form-data helper that includes auth and tenant headers and leaves Content-Type to the browser
export async function apiRequestForm(
  method: string,
  url: string,
  formData: FormData,
): Promise<Response> {
  const tenantHeaders = getTenantHeaders();
  
  // Ensure URL starts with /api/v1 if it's a relative path
  const fullUrl = url.startsWith('/api/v1') ? getApiUrl(url.replace('/api/v1', '')) : url;
  
  const headers: Record<string, string> = {
    ...tenantHeaders,
  };
  
  const token = await getIdTokenSafe();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  
  /*console.log('🔍 QueryClient Form API Call Debug:', {
    method,
    url: fullUrl,
    hasToken: !!token,
    tenantHeaders
  });*/
  
  const res = await fetch(fullUrl, { method, headers, body: formData });
  
  /*console.log('🔍 QueryClient Form API Response:', {
    status: res.status,
    statusText: res.statusText,
    url: res.url
  });*/
  
  await throwIfResNotOk(res);
  return res;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
